import Avatar from 'components/Avatar'
import { FormGroup } from 'components/inputs'
import {
  CustomSelect,
  ItemSource,
  SingleItemSelectedDefault,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import Member from 'iam/models/Member'
import { TeamSelection } from 'jobs/creation/DraftChange'
import { observer } from 'mobx-react'
import * as React from 'react'
import TeamSelectorViewModel from '../../../../TeamSelectorViewModel'

const myselfDefaultPlaceholder = 'Myself'

interface CompactTeamSelectorProps {
  vm: TeamSelectorViewModel
}

export default observer(function CompactTeamSelector({
  vm,
}: CompactTeamSelectorProps) {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.availableTeams, (item, text) => {
        const sourceText = item === TeamSelection.myself ? 'Myself' : item.name
        return sourceText.toLowerCase().includes(text.toLowerCase())
      }),
    [vm.availableTeams],
  )
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  const nullSelectionPlaceholder = vm.nullSelectionPlaceholder

  return (
    <FormGroup>
      <CustomSelect
        {...selectProps}
        placeholder="Select a Team"
        itemToString={(v) =>
          v === TeamSelection.myself
            ? nullSelectionPlaceholder
            : String(v?.name)
        }
        disabled={vm.disabled}
        focus$={vm.step.becomeActive$}
        onFocus={vm.step.onBecameActive}
        value={vm._selectedTeam ?? undefined}
        onChange={vm.selectTeam}
        multi={false}
        data-test="compact-team-selector"
        renderItem={(itemProps) => {
          const display = getDisplayPropsForTeam(
            vm.workspaceMember ?? Member.none,
            itemProps.item,
            nullSelectionPlaceholder,
          )
          return (
            <SingleItemSelectedDefault
              visual={
                display.avatar && (
                  <Avatar
                    size={24}
                    subject={{
                      displayName: display.name,
                      avatar: display.avatar,
                    }}
                  />
                )
              }
              {...itemProps}
            />
          )
        }}
        renderSearchItem={({ item, itemToString, ...itemProps }) => {
          const display = getDisplayPropsForTeam(
            vm.workspaceMember ?? Member.none,
            item,
            nullSelectionPlaceholder,
          )
          return (
            <SearchResultItem
              {...itemProps}
              visual={
                display.avatar && (
                  <Avatar
                    size={24}
                    subject={{
                      avatar: display.avatar,
                      displayName: display.name,
                    }}
                  />
                )
              }
            >
              {display.name}
            </SearchResultItem>
          )
        }}
      />
    </FormGroup>
  )
})

/**
 * Gets display props for a team.
 */
function getDisplayPropsForTeam(
  member: Member,
  team: TeamSelection,
  nullSelectionPlaceholder: string,
) {
  if (team === TeamSelection.myself) {
    return {
      name: nullSelectionPlaceholder,
      avatar:
        nullSelectionPlaceholder === myselfDefaultPlaceholder
          ? member.avatar
          : null,
    }
  }

  return {
    name: team.name,
    avatar: team.avatar,
  }
}
